import {Component, OnInit} from '@angular/core';
import {CashPointHttpService} from "../_services/cash-point-http.service";
import {CashPointEntryDto} from "../_models/cash-point-entry-dto";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {CashPointEntryState, CashPointEntryStateLabels} from "../_models/_enums/cash-point-entry-state";
import {DeviceNl} from "../_models/device-nl";
import {CurrencyPipe, DatePipe, DecimalPipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {EmployeeNl} from "../../company/x-models/employee-nl";
import {EmployeeHttpService} from "../../company/x-http-requests/employee-http.service";
import {PermissionService} from "../../../_auth/permission.service";
import {BookingType, BookingTypeLabels} from "../../../models/payment/booking-type";
import {CurrencyInputComponent} from "../../../x-components/currency-input/currency-input.component";
import {FormsModule} from "@angular/forms";
import {BusinessCaseInformation} from "../_models/business-case-information";
import {BusinessCaseType, BusinessCaseTypeLabels} from "../_models/_enums/business-case-type";
import {KassenvorgangShape} from "../../../_shared/calculation/kassenvorgang-shape";
import {HttpErrorResponse, HttpEventType, HttpResponse} from "@angular/common/http";
import {RouterLink} from "@angular/router";
import {AppRoutes} from "../../../enums/routes";
import {LocationRoutes} from "../_models/_enums/location-routes";
import {SelectGroup, SelectionMode} from "../../../models/_generic/select-group";

@Component({
  selector: 'app-cash-points',
  imports: [
    NgForOf,
    DatePipe,
    NgClass,
    NgIf,
    CurrencyPipe,
    CurrencyInputComponent,
    DecimalPipe,
    FormsModule,
    RouterLink
  ],
  templateUrl: './cash-points.component.html',
  styleUrl: './cash-points.component.scss'
})
export class CashPointsComponent implements OnInit {
  selectGroup = new SelectGroup<CashPointEntryDto>();
  combinedFile: boolean = true;
  entries: CashPointEntryDto[] | undefined | null = undefined;
  grouped_entries: { [key: number]: CashPointEntryDto[] } = {};
  devices: { [key: string]: DeviceNl } = {};
  employees: { [key: string]: EmployeeNl } = {};
  closing_summary: { [key: number]: number } = {};
  closing_business_case_types: { [key: number]: number } = {};
  actual: number | null = null;
  comment: string = "";
  protected readonly CashPointEntryStateLabels = CashPointEntryStateLabels;
  protected readonly CashPointEntryState = CashPointEntryState;
  protected readonly PermissionService = PermissionService;
  protected readonly BookingTypeLabels = BookingTypeLabels;
  protected readonly BookingType = BookingType;
  protected readonly Routes = AppRoutes;
  protected readonly LocationRoutes = LocationRoutes;
  protected readonly SelectionMode = SelectionMode;
  protected readonly BusinessCaseTypeLabels = BusinessCaseTypeLabels;
  protected readonly BusinessCaseType = BusinessCaseType;
  private today = new Date();

  constructor(private cashPointHttpService: CashPointHttpService,
              private employeeHttpService: EmployeeHttpService,
              private gas: GlobalAlertService,
              public permissionService: PermissionService) {
  }

  private _start: Date = new Date(this.today.getFullYear(), this.today.getMonth(), 1)

  get start(): Date {
    return this._start;
  }

  set start(value: Date) {
    this._start = value;
    this.load();
  }

  private _end: Date = new Date(this.today.getFullYear(), this.today.getMonth() + 1, 1);

  get end(): Date {
    return this._end;
  }

  set end(value: Date) {
    this._end = value;
  }

  // closing the cash point
  private _closing: CashPointEntryDto | undefined = undefined;

  get closing(): CashPointEntryDto | undefined {
    return this._closing;
  }

  set closing(value: CashPointEntryDto | undefined) {
    this._closing = value;
    if (this._closing) {
      this.getSummary(this._closing);
      this.getBusinessCaseTypeSummary(this._closing);
      //this.getLastClosed(this._closing.device.id);
    } else {
      this.reset_closing_values();
    }
  }

  private _skimming_difference: number | null = null;

  get skimming_difference(): number | null {
    return this._skimming_difference;
  }

  set skimming_difference(value: number | null) {
    this._skimming_difference = value == 0 ? null : value;
  }

  private _skimming_result: number | null = null;

  get skimming_result(): number | null {
    return this._skimming_result;
  }

  set skimming_result(value: number | null) {
    this._skimming_result = value;
  }

  ngOnInit(): void {
    this.load()
  }

  // end needs to be set before start, otherwise payload will be wrong
  setMonth(amount: number) {
    if (amount == 0) {
      this.end = new Date(this.today.getFullYear(), this.today.getMonth() + 1, 1);
      this.start = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
      return;
    }

    this.end = new Date(this.start.getFullYear(), this.start.getMonth() + amount + 1, 1);
    this.start = new Date(this.start.getFullYear(), this.start.getMonth() + amount, 1);
  }

  load() {
    this.entries = undefined;
    this.cashPointHttpService.list(undefined, this.start, this.end, undefined).subscribe({
      next: (value) => {
        this.entries = value;
        this.group();
        this.selectGroup.update(this.entries, (a, b) => a.id == b.id);
      },
      error: err => {
        console.error(err);
        this.entries = null;
        this.gas.createAndShow("Fehler", "Fehler beim Laden der Daten", AlertLevel.error);
      }
    })
    // all=true: historical cash-point entries booked by now-former employees must still resolve to a name.
    // stats=false: this screen only ever reads `.name` out of the map below — the worktime statistics, employee
    // attributes and Telegram link are dead weight here, and computing them dominated the request (#457).
    this.employeeHttpService.listNl(undefined, true, false).subscribe({
      next: (value) => {
        this.employees = value.reduce((acc, emp) => {
          acc[emp.id] = emp;
          return acc;
        }, {} as { [key: string]: EmployeeNl })
      },
      error: err => {
        console.error(err);
        this.employees = {};
        this.gas.createAndShow("Fehler", "Fehler beim Laden der Mitarbeiter", AlertLevel.error);
      }
    })
  }

  group() {
    if (!this.entries) return;
    this.grouped_entries = this.entries
      .sort((a, b) => a.created < b.created ? 1 : -1)
      .reduce((acc, entry) => {
        if (!acc[entry.state]) acc[entry.state] = [];
        acc[entry.state].push(entry);
        return acc;
      }, {} as { [key: number]: CashPointEntryDto[] })

    this.devices = this.entries
      .filter(x => x.device.serialNumber && x.device.serialNumber != '')
      .sort((a, b) => a.created < b.created ? 1 : -1)
      .reduce((acc, entry) => {
        if (!acc[entry.device.serialNumber!]) {
          acc[entry.device.serialNumber!] = entry.device;
          acc[entry.device.serialNumber!].cashPointEntries = [entry];
        } else if (!acc[entry.device.serialNumber!].active && entry.active) {
          entry.device.cashPointEntries = acc[entry.device.serialNumber!].cashPointEntries
          acc[entry.device.serialNumber!] = entry.device;
        } else {
          acc[entry.device.serialNumber!].cashPointEntries.push(entry);
        }
        return acc;
      }, {} as { [key: string]: DeviceNl })
  }

  getGroups() {
    return Object.keys(this.grouped_entries).map(x => parseInt(x)).sort((a, b) => {
      if (a == b) return 0;
      if (a == 10) return -1;
      if (b == 10) return -1;
      return a - b;
    });
  }

  getSummary(entry: CashPointEntryDto) {
    this.cashPointHttpService
      .getSummary(entry.device.id, entry.id)
      .withLoadingIndicator()
      .subscribe({
        next: (value) => {
          this.closing_summary = value;
          Object.keys(this.closing_summary).map(x => parseInt(x)).filter(x => x > 1).forEach(x => {
            delete this.closing_summary[x];
          })
        },
        error: err => {
          console.error(err);
          this.closing_summary = {};
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Zusammenfassung", AlertLevel.error);
          this.closing = undefined;
        }
      })
  }

  getBusinessCaseTypeSummary(entry: CashPointEntryDto) {
    this.cashPointHttpService.getBusinessCaseTypeSummary(entry.device.id, entry.id)
      .withLoadingIndicator()
      .subscribe({
        next: (value) => {
          this.closing_business_case_types = value;
          // Object.keys(value).map(x => parseInt(x)).filter(x => x > 0).forEach(x => {
          //   delete value[x];
          // })
          // #836: decided by how the till was opened — its float after an Anfangsbestand, an empty drawer after a
          // Geldtransit. The register proposes the same figure.
          const skimTarget = CashPointEntryDto.skimTarget(entry, value);
          if (skimTarget !== undefined) {
            this._skimming_result = skimTarget.toCurrencyFraction();
          }
        },
        error: err => {
          console.error(err);
          this.closing_business_case_types = {};
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Zusammenfassung", AlertLevel.error);
          this.closing = undefined;
        }
      })
  }

  getLastClosed(device: string) {
    this.cashPointHttpService.getLastClosed(device)
      .withLoadingIndicator()
      .subscribe({
        next: (value) => {
          if (value[BookingType.Cash] && value[BookingType.Cash] != 0) {
            this._skimming_result = value[BookingType.Cash];
          }
        }
      })
  }

  getBookingTypes() {
    return Object.keys(this.closing_summary).map(x => parseInt(x));
  }

  getBusinessCaseTypes() {
    return Object.keys(this.closing_business_case_types).map(x => parseInt(x));
  }

  reset_closing_values() {
    this.actual = null;
    this.skimming_difference = null;
    this.skimming_result = null;
    this.comment = "";
  }

  close() {
    if (!this.closing) return;

    // todo niko: check if this is correct
    //if ((this.actual ?? this.closing_summary[BookingType.Cash] ?? 0) < 0) {
    //  this.gas.createAndShow("Fehler", "Der Kassenbestand darf nicht negativ sein.", AlertLevel.error);
    //  return;
    //}

    if ((this.skimming_result ?? ((this.actual ?? this.closing_summary[BookingType.Cash] ?? 0) - (this.skimming_difference ?? 0))) < 0) {
      this.gas.createAndShow("Fehler", "Der Kassenbestand darf nicht negativ sein.", AlertLevel.error);
      return;
    }

    let actual = (this.actual ?? this.closing_summary[BookingType.Cash] ?? 0);
    let skimming_result = this.skimming_result ?? ((this.actual ?? this.closing_summary[BookingType.Cash] ?? 0) - (this.skimming_difference ?? 0));

    let bcs: BusinessCaseInformation[] = [];

    // Both postings are minted by the shared factories (`KassenvorgangShape`), the same ones closing-model.ts
    // uses for the assistant, so the two web closes, the register and the server agree on the stored shape
    // (#820: the signed money on `amount`, `quantity` +1), the transit's qualifier (#820: Abschöpfung out,
    // Einlage in) and the difference's key (#822: a surplus at the standard rate, a shortfall not taxable).
    // `difference` is Soll − Ist, the signed money its negation: positive = surplus.
    // Rounded to the cent before the zero check, as the assistant's ClosingModel does: an unrounded float
    // difference of 0.30000000000000004 − 0.30 is not 0 and posted a DifferenzSollIst that the server then
    // quantized to 0,00 — a row for a difference that does not exist.
    let difference = ((this.closing_summary[BookingType.Cash] ?? 0) - actual).toCurrencyFraction();
    if (difference != 0) {
      bcs.push(KassenvorgangShape.kassendifferenz(-difference, this.comment ? this.comment : null));
    }

    // Endbestand − Ist: negative for cash taken out, positive for cash put in. Rounded for the same reason.
    let skimming = (skimming_result - actual).toCurrencyFraction();
    if (skimming != 0) {
      bcs.push(KassenvorgangShape.geldtransit(skimming));
    }

    this.cashPointHttpService.close(this.closing.device.id, this.closing.id, bcs)
      .withLoadingIndicator()
      .subscribe({
        next: (value) => {
          this.gas.createAndShow("Erfolg", "Kassenabschluss erfolgreich", AlertLevel.success);
          this.closing = undefined;
          this.reset_closing_values();
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.gas.createAndShow("Fehler", this.closeErrorMessage(err), AlertLevel.error);
        }
      })
  }

  /**
   * Maps a failed cash-point close request to a German user-facing message. Branches on the structured
   * `ApiError.code` (same pattern as cash-points-assistant-closing.component.ts's closeErrorMessage) for known
   * guard failures, falling back to the existing generic message for anything else so unrelated failure modes
   * are unaffected.
   */
  private closeErrorMessage(error: HttpErrorResponse): string {
    switch (error?.error?.code) {
      case "E_CASHPOINTENTRY_PENDING_BOOKINGS":
        return `Für dieses Gerät werden noch Buchungen verarbeitet. Bitte kurz warten und den Kassenschluss für '${this.closing?.device.name}' erneut versuchen.`;
      default:
        return "Fehler beim Abschließen der Kasse";
    }
  }

  getValueOrZero(value: number | undefined): number {
    return value ?? 0;
  }

  downloadMany() {
    this.cashPointHttpService.summary(this.selectGroup.items.map(x => x.id), this.combinedFile)
      .withLoadingIndicator()
      .subscribe({
        next: (value) => {
          if (value.type === HttpEventType.Response) {
            this._download(value);
          }
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Herunterladen der Zusammenfassung", AlertLevel.error);
        }
      })
  }

  download(entry: CashPointEntryDto) {
    this.cashPointHttpService.summary([entry.id]).subscribe({
      next: (value) => {
        if (value.type === HttpEventType.Response) {
          this._download(value);
        }
      },
      error: err => {
        console.error(err);
        this.gas.createAndShow("Fehler", "Fehler beim Herunterladen der Zusammenfassung", AlertLevel.error);
      }
    })
  }

  private _download = (data: HttpResponse<Blob>) => {
    const file = new Blob([data.body!], {type: data.body?.type});
    const link = document.createElement('a');
    link.setAttribute('style', 'display: none');
    document.body.appendChild(link);
    link.href = window.URL.createObjectURL(file);
    link.target = '_blank';

    const header = data.headers.get('content-disposition');
    const regex = /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/;
    const match = regex.exec(header || '');

    link.download = match ? match[1] : 'Zusammenfassung.pdf';

    link.click();
    document.body.removeChild(link);
  }
}
